import { observer } from 'mobx-react';
import { Redirect, useHistory } from 'react-router';
import { useRef, useEffect } from 'react';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { Roles } from '@roc/feature-utils';
import { useRoutes, useStore } from '../../hooks';

interface LenderDetailsProps {
  onNavigateToUserProfile?: () => void;
}

export const LenderDetails = observer(({ onNavigateToUserProfile }: LenderDetailsProps) => {
  const { companyProfileRoutesConfig, dashboardRoutesConfig } = useRoutes();
  const { companyProfileStore } = useStore();
  const { lenderDetailsStore } = companyProfileStore;
  const { userStore } = useUserStore();
  const { globalStore } = useBaseStore();
  const ref = useRef<HTMLElement>(null);
  const { push } = useHistory();

  const canAccessOnboarding =
    globalStore.userFeatures?.backOfficeUser ||
    globalStore.userFeatures?.adminPortalConfiguration ||
    [Roles.ADMIN].some(role =>
      userStore.userInformation?.roles?.includes(role)
    );

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const { eventName } = e.detail;
      if (eventName === 'navigateToUserProfile') {
        userStore.getUserInfoAndEntitlements(() => {
          onNavigateToUserProfile?.();
          lenderDetailsStore.salesforceLenderStore.fetchSalesforceLender(true);
        });
      } else if (eventName === 'lenderOnboardingCompleted') {
        lenderDetailsStore.salesforceLenderStore.fetchSalesforceLender(true);
        userStore.getUserInfoAndEntitlements();
        redirect();
      }
    };
    const node = ref.current;
    node?.addEventListener('platform-ui-event', handler);
    return () => node?.removeEventListener('platform-ui-event', handler);
  }, [onNavigateToUserProfile, canAccessOnboarding, !!userStore.userInformation]);

  const redirect = () => {
    push(
      (globalStore.userFeatures?.adminPortalConfiguration || globalStore.userFeatures?.backOfficeUser)
        ? companyProfileRoutesConfig.companyProfile.url : dashboardRoutesConfig.dashboard.url
    );
  }

  if (!userStore.userInformation) {
    return null;
  }

  if (!canAccessOnboarding) {
    return <Redirect to={dashboardRoutesConfig.dashboard.url} />;
  }

  return (
    <platform-ui
      id="platform-ui-lender-onboarding"
      component="lender_onboarding"
      ref={ref}
    />
  );
});
